import { useEffect, useState } from "react";

import { ITestAnswer, ITestAnswerTimes } from "@/shared/components/charts/types/chartTypes";

import { minToSec, secToMls } from "@/shared/utils/common.utils";
import { IAssessmentInvite } from "@/shared/hooks/initializer/use-initialize-candidate.hook";

import { ITest, ILocalizedTestElement } from "@/modules/assessments/types/test.interface";
import { InvitesHttpService } from "@/modules/candidate/services/InvitesHttpService";

import { getTestElementSection, getTestTimes } from "../utils/section.utils";
import { TIMER_ERROR_INITIAL_VALUE, processTimerErrorCode, TimerError } from "../utils/timerError.utils";

export const DELTA_TIME_SEC = 15;

export function useSectionsHook({
  test,
  invite,
  answers,
  currentPage,
  questionsList,
  isOpenedInstructions,
  isVisitingInstructionsPageFirstTime,
}: {
  currentPage: number;
  answers: ITestAnswer[];
  questionsList: ILocalizedTestElement[][];
  isOpenedInstructions: boolean;
  test?: ITest;
  invite?: IAssessmentInvite;
  isVisitingInstructionsPageFirstTime?: boolean;
}) {
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const [isTimedOutSection, setIsTimedOutSection] = useState(false);
  const [sectionsTime, setSectionsTime] = useState<ITestAnswerTimes>({});
  const [currentSectionElement, setCurrentSectionElement] = useState<ILocalizedTestElement | undefined>();
  const [timerError, setTimerError] = useState<TimerError>(TIMER_ERROR_INITIAL_VALUE);
  const [isOpenedTimeOutModal, setIsOpenedTimeOutModal] = useState<boolean>(false);

  const sectionName = currentSectionElement?.config.startsSection?.name;
  const testAnswer = answers.find((answer) => answer.testId === test?.testId);
  const isViewingInstructionsModalForFirstTime = isOpenedInstructions && (!testAnswer || !testAnswer.answer.length);

  // 1. Define section
  useEffect(() => {
    if (!currentPage || !test?.testId) {
      return;
    }

    setCurrentSectionElement(getTestElementSection(questionsList, test.testId, currentPage) || undefined);
  }, [currentPage, test?.testId, questionsList]);

  // 2. Set default times
  useEffect(() => {
    if (!answers.length || !test?.testId) {
      return;
    }

    setSectionsTime(getTestTimes(answers, test.testId) || {});
  }, [answers, test?.testId, sectionName]);

  // 3. Set timer for the section
  useEffect(() => {
    const startsSection = currentSectionElement?.config?.startsSection;
    if (!startsSection || !startsSection.timeLimit || invite?.timerDisabled || isVisitingInstructionsPageFirstTime) {
      setSectionsTime({});
      return;
    }

    if (!invite?.inviteId || !test?.testId || isViewingInstructionsModalForFirstTime) {
      return;
    }

    const sectionKey = currentSectionElement?.config.key;

    const interval = setInterval(async () => {
      try {
        setTimerError(TIMER_ERROR_INITIAL_VALUE);
        const sectionTimer = await InvitesHttpService.increaseSectionTime(invite.inviteId, test.testId, {
          elementKey: sectionKey,
          deltaTime: DELTA_TIME_SEC,
        });

        setSectionsTime((section) => ({ ...section, [sectionKey]: sectionTimer }));
      } catch (e: any) {
        const errorCode = e?.response?.data?.statusCode;
        const errorResult = processTimerErrorCode(errorCode);

        if (errorResult.isTimeout) {
          setIsOpenedTimeOutModal(true);
        } else {
          setTimerError(errorResult);
        }
      }
    }, secToMls(DELTA_TIME_SEC));

    if (isTimedOutSection) {
      clearInterval(interval);
    }

    return () => clearInterval(interval);
  }, [
    currentSectionElement,
    isTimedOutSection,
    test?.testId,
    invite?.inviteId,
    invite?.timerDisabled,
    isViewingInstructionsModalForFirstTime,
    isVisitingInstructionsPageFirstTime,
  ]);

  // 4. Check if the section is timed-out
  useEffect(() => {
    if (!currentSectionElement || !currentSectionElement.config.startsSection?.timeLimit || invite?.timerDisabled) {
      setIsTimedOutSection(false);
    }

    const startsSection = currentSectionElement?.config?.startsSection;

    if (!startsSection || !invite?.inviteId || !test?.testId || typeof startsSection.timeLimit !== "number") {
      return;
    }

    const sectionKey = currentSectionElement?.config.key;

    if (
      startsSection.timeLimit &&
      sectionsTime[sectionKey] &&
      sectionsTime[sectionKey].time >= minToSec(startsSection.timeLimit)
    ) {
      setIsTimedOutSection(true);
    } else {
      setIsTimedOutSection(false);
    }
  }, [sectionName, sectionsTime, invite?.timerDisabled]); // eslint-disable-line react-hooks/exhaustive-deps

  // 5. How much time left for the section
  useEffect(() => {
    if (currentSectionElement?.config.startsSection?.timeLimit && !invite?.timerDisabled) {
      let subtractor = 0;

      if (sectionsTime[currentSectionElement?.config.key]?.time) {
        subtractor = sectionsTime[currentSectionElement?.config.key]?.time;
      }

      const timeLeft = secToMls(minToSec(currentSectionElement?.config?.startsSection?.timeLimit) - subtractor);

      if (
        isVisitingInstructionsPageFirstTime ||
        (isViewingInstructionsModalForFirstTime &&
          timeLeft === secToMls(minToSec(currentSectionElement?.config?.startsSection?.timeLimit)))
      ) {
        return setTimeLeft(null);
      }
      setTimeLeft(timeLeft);
    } else {
      setTimeLeft(null);
    }
  }, [
    currentSectionElement,
    sectionsTime,
    isViewingInstructionsModalForFirstTime,
    invite?.timerDisabled,
    isVisitingInstructionsPageFirstTime,
  ]);

  // 6. Check if the section is timed out
  useEffect(() => {
    if (!isTimedOutSection) {
      return;
    }

    setIsOpenedTimeOutModal(true);
  }, [isTimedOutSection]);

  return {
    timeLeft,
    timerError,
    sectionsTime,
    isTimedOutSection,
    currentSectionElement,
    isOpenedTimeOutModal,
    setIsOpenedTimeOutModal,
  };
}
